<template>
  <div>
    <div class="operations">
      <a-select v-model:value="status" style="width: 180px; margin-right: 12px" @change="loadCases">
        <a-select-option value="">全部状态</a-select-option>
        <a-select-option value="pending_review">待审核</a-select-option>
        <a-select-option value="shooting">拍摄中</a-select-option>
        <a-select-option value="retake">待补拍</a-select-option>
        <a-select-option value="approved">素材合格</a-select-option>
      </a-select>
      <a-select v-model:value="sortBy" style="width: 200px; margin-right: 12px" @change="loadCases">
        <a-select-option value="created_desc">最新案例</a-select-option>
        <a-select-option value="douyin_play_desc">抖音播放 ↓</a-select-option>
        <a-select-option value="douyin_interact_desc">互动率 ↓</a-select-option>
      </a-select>
      <a-button @click="loadCases">刷新</a-button>
      <a-button
        danger
        style="margin-left: 12px"
        :disabled="!selectedCaseIds.length"
        :loading="deleting"
        @click="deleteSelected"
      >
        删除选中{{ selectedCaseIds.length ? ` (${selectedCaseIds.length})` : '' }}
      </a-button>
    </div>

    <a-alert
      v-if="loadError"
      type="error"
      show-icon
      :message="loadError"
      style="margin-bottom: 12px"
    >
      <template #action>
        <a-button size="small" @click="loadCases">重试</a-button>
      </template>
    </a-alert>

    <giga-table
      :gData="dataSource"
      :gColumns="columns"
      :loading="loading"
      :rowSelection="rowSelection"
    />

    <a-drawer
      v-model:visible="showDetail"
      width="1280"
      placement="right"
      class="cc-drawer"
    >
      <template #title>
        <div class="cc-drawer-title">{{ detailTitle }}</div>
        <div v-if="currentCase" class="cc-drawer-sub">
          {{ submitterText }} · {{ shootTimeText }} · {{ currentCase.progress.completed }}/{{ currentCase.progress.required }}
        </div>
      </template>
      <template v-if="currentCase && !isApprovedCase" #extra>
        <a-button
          v-if="currentCase.status === 'pending_review'"
          type="primary"
          style="margin-right: 8px"
          @click="approveCase"
        >
          素材合格
        </a-button>
        <a-button danger :disabled="!retakeShotCodes.length" @click="retakeCase">
          要求补拍{{ retakeShotCodes.length ? ` (${retakeShotCodes.length})` : '' }}
        </a-button>
      </template>
      <a-spin :spinning="detailLoading">
        <div v-if="currentCase" class="cc-body">
          <div class="cc-main">
            <div class="cc-section-head">
              <span class="cc-section-title">拍摄素材</span>
              <span class="cc-section-sub">
                {{ uploadedAssets.length }} 个已上传 ·
                视频 {{ videoAssets.length }} · 照片 {{ photoAssets.length }}
              </span>
              <a-button
                v-if="pickedShotCodes.length"
                danger
                size="small"
                :loading="assetDeleting"
                @click="deleteAssets(pickedShotCodes)"
              >
                删除选中 ({{ pickedShotCodes.length }})
              </a-button>
            </div>

            <a-alert
              v-if="showCaseAlert"
              :type="caseAlertType"
              show-icon
              style="margin-bottom: 12px"
            >
              <template #message>{{ caseAlertMessage }}</template>
              <template #description>
                <div v-for="asset in retakeRequiredAssets" :key="'r-' + asset.shotCode" class="retake-line">
                  待补拍 · {{ asset.displayIndex }} {{ asset.shotName }}
                  <span v-if="asset.retakeNote">：{{ asset.retakeNote }}</span>
                  <span v-else>（未填写说明）</span>
                </div>
                <div v-if="missingAssets.length" class="retake-line">
                  未上传：{{ missingAssets.map(a => a.shotName).join('、') }}
                </div>
              </template>
            </a-alert>

            <div class="asset-grid">
              <div
                v-for="asset in sortedAssets"
                :key="asset.shotCode"
                class="asset-card"
                :class="{
                  'asset-card-active': retakeShotCodes.includes(asset.shotCode),
                  'asset-card-retake': asset.status === 'retake_required'
                }"
              >
                <div class="asset-media">
                  <a-checkbox
                    v-if="asset.fileUrl"
                    class="asset-pick"
                    :checked="pickedShotCodes.includes(asset.shotCode)"
                    @change="togglePick(asset.shotCode)"
                  />
                  <video
                    v-if="asset.fileUrl && asset.mediaType !== 'photo'"
                    :src="asset.fileUrl"
                    controls
                    preload="metadata"
                    playsinline
                  />
                  <img v-else-if="asset.fileUrl" :src="asset.fileUrl" />
                  <div v-else class="empty-media">未上传</div>
                </div>
                <div class="asset-info">
                  <div class="asset-name">
                    {{ asset.displayIndex }}
                    {{ asset.shotName }}
                  </div>
                  <div class="asset-tags">
                    <a-tag :color="statusColor(asset.status)">{{ statusText(asset.status) }}</a-tag>
                    <a-tag v-if="asset.captureSource === 'library'" color="purple">公共素材</a-tag>
                    <a-tag v-if="asset.category === 'extra'" color="cyan">额外</a-tag>
                    <a-tag v-if="asset.category === 'cover'" color="gold">封面</a-tag>
                  </div>
                  <div v-if="asset.voiceTranscript" class="asset-say">
                    “{{ asset.voiceTranscript }}”
                  </div>
                  <div v-if="asset.status === 'retake_required' && asset.retakeNote" class="asset-retake-note">
                    补拍要求：{{ asset.retakeNote }}
                  </div>
                  <div v-if="asset.captureFeedback && !asset.captureFeedback.stale" class="asset-capture-feedback">
                    <div class="asset-capture-feedback-head">
                      <a-tag :color="captureFeedbackColor(asset.captureFeedback.verdict)">
                        {{ captureFeedbackLabel(asset.captureFeedback.verdict) }}
                      </a-tag>
                      <span v-if="asset.captureFeedback.source" class="asset-capture-feedback-source">
                        {{ asset.captureFeedback.source }}
                      </span>
                    </div>
                    <div>{{ asset.captureFeedback.summary }}</div>
                    <div v-if="asset.captureFeedback.issues && asset.captureFeedback.issues.length" class="asset-capture-feedback-sub">
                      问题：{{ asset.captureFeedback.issues.join('；') }}
                    </div>
                    <div v-if="asset.captureFeedback.suggestions && asset.captureFeedback.suggestions.length" class="asset-capture-feedback-sub">
                      建议：{{ asset.captureFeedback.suggestions.join('；') }}
                    </div>
                  </div>
                  <div class="asset-foot">
                    <span>{{ durationText(asset) }}{{ asset.capturedAt ? ` · ${timeText(asset.capturedAt)}` : '' }}</span>
                    <span class="asset-links">
                      <a v-if="asset.fileUrl" :href="asset.fileUrl" target="_blank">下载</a>
                      <a v-if="!isApprovedCase" @click="pickRetake(asset)">
                        {{ retakeShotCodes.includes(asset.shotCode) ? '取消补拍' : '标记补拍' }}
                      </a>
                      <a v-if="asset.fileUrl" class="danger" @click="deleteAssets([asset.shotCode])">删除</a>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="currentCase" class="cc-panel cc-case-info">
              <div class="cc-panel-title">案例资料</div>
              <div v-for="row in caseInfoRows" :key="row.label" class="cc-kv cc-kv-wide">
                <span>{{ row.label }}</span>
                <b :class="{ 'cc-kv-muted': row.muted }">{{ row.value }}</b>
              </div>
              <div class="cc-other-notes-block">
                <div class="cc-other-notes-label">其他</div>
                <div :class="['cc-other-notes-body', { 'cc-kv-muted': !otherNotesText }]">
                  {{ otherNotesText || '（未填写）' }}
                </div>
              </div>
            </div>
          </div>

          <div class="cc-side">
            <div v-if="currentCase && showCaptureFeedbackPanel" class="cc-panel cc-panel-feedback">
              <div class="cc-panel-title">拍摄反馈</div>
              <div v-if="currentCase.caseCaptureFeedback && !currentCase.caseCaptureFeedback.stale" class="cc-case-feedback-read">
                <div class="cc-case-feedback-read-title">整单反馈</div>
                <div>{{ currentCase.caseCaptureFeedback.summary }}</div>
              </div>
              <div class="cc-feedback-subtitle">整单反馈（一句话）</div>
              <a-textarea v-model:value="caseFeedbackForm.summary" :rows="2" placeholder="例如：整体可用，SHOT03、07 建议补拍" />
              <a-button block :loading="caseFeedbackSaving" style="margin: 16px 0" @click="submitCaseCaptureFeedback">
                保存整单反馈
              </a-button>
              <div class="cc-feedback-subtitle">单镜反馈</div>
              <a-select
                v-model:value="feedbackShotCode"
                placeholder="选择镜头"
                style="width: 100%; margin-bottom: 8px"
                :options="feedbackShotOptions"
              />
              <a-select v-model:value="feedbackForm.verdict" style="width: 100%; margin-bottom: 8px">
                <a-select-option value="pass">pass · 可用</a-select-option>
                <a-select-option value="retake">retake · 建议重拍</a-select-option>
                <a-select-option value="review">review · 待确认</a-select-option>
              </a-select>
              <a-textarea v-model:value="feedbackForm.summary" :rows="2" placeholder="一句话结论（必填）" />
              <a-textarea v-model:value="feedbackForm.issuesText" :rows="2" placeholder="问题，每行一条" style="margin-top: 8px" />
              <a-textarea v-model:value="feedbackForm.suggestionsText" :rows="2" placeholder="优化建议，每行一条" style="margin-top: 8px" />
              <a-checkbox v-if="feedbackForm.verdict === 'retake'" v-model:checked="feedbackForm.markRetake" style="margin: 8px 0">
                同步标记为待补拍
              </a-checkbox>
              <a-button type="primary" block :loading="feedbackSaving" :disabled="!feedbackShotCode" @click="submitCaptureFeedback">
                写入拍摄反馈
              </a-button>
            </div>
            <div v-if="showVideoSidePanel" class="cc-panel cc-panel-video">
              <div class="cc-panel-title">
                短视频
                <a-tag v-if="latestVideo" :color="videoStatusColor">{{ videoStatusText }}</a-tag>
              </div>

              <template v-if="latestReadyVideo && latestReadyVideo.ossUrl">
                <a-alert
                  v-if="isRegeneratingVideo"
                  type="info"
                  message="新版本短视频正在制作中，下方为当前可用成片。"
                  show-icon
                  style="margin-bottom: 12px"
                />
                <div class="cc-video-section">
                  <div class="cc-section-label">① 成片预览</div>
                  <video
                    :src="latestReadyVideo.ossUrl"
                    controls
                    playsinline
                    style="width: 100%; border-radius: 8px; background: #000;"
                  />
                  <div class="cc-kv"><span>选题</span><b>{{ latestReadyVideo.topicType || '—' }} {{ latestReadyVideo.title || '' }}</b></div>
                  <div class="cc-kv"><span>时长</span><b>{{ readyVideoDurationText }}</b></div>
                  <a :href="latestReadyVideo.ossUrl" target="_blank">新窗口打开成片</a>
                </div>

                <div class="cc-video-section">
                  <div class="cc-section-label">② 抖音文案</div>
                  <div class="cc-douyin-caption">
                    <div class="cc-douyin-caption-head">
                      <span>{{ isDouyinPublished ? '发布时使用的文案' : '发布前可编辑' }}</span>
                      <span v-if="latestReadyVideo.douyinCaptionSource === 'ai' && !isDouyinPublished" class="cc-caption-tag cc-caption-tag-ai">AI 生成</span>
                      <span v-if="latestReadyVideo.douyinCaptionSource === 'manual' && !isDouyinPublished" class="cc-caption-tag">已人工编辑</span>
                    </div>
                    <a-textarea
                      v-if="canEditDouyinCaption"
                      v-model:value="douyinCaptionDraft"
                      :rows="4"
                      placeholder="正文 + #话题，例：贴完车衣质感拉满 #蔚来ES9 #隐形车衣"
                    />
                    <div v-else class="cc-caption-readonly">{{ douyinCaptionDraft || '（无文案）' }}</div>
                    <div v-if="canEditDouyinCaption" class="cc-douyin-caption-actions">
                      <a-button size="small" :loading="douyinCaptionSaving" @click="saveDouyinCaption">
                        保存文案
                      </a-button>
                      <a-button size="small" :loading="douyinCaptionSaving" @click="regenerateDouyinCaption">
                        重新生成文案
                      </a-button>
                    </div>
                  </div>
                </div>

                <div v-if="isDouyinPublished" class="cc-video-section">
                  <div class="cc-section-label">③ 抖音作品</div>
                  <div class="cc-douyin-link-panel">
                    <div class="cc-kv cc-kv-wide">
                      <span>状态</span>
                      <b><a-tag color="success">已发布</a-tag></b>
                    </div>
                    <div class="cc-kv cc-kv-wide">
                      <span>作品 ID</span>
                      <b>{{ douyinDisplayVideo.douyinItemId }}</b>
                    </div>
                    <div v-if="douyinShareUrl" class="cc-kv cc-kv-wide">
                      <span>链接</span>
                      <b>
                        <a :href="douyinShareUrl" target="_blank" rel="noopener noreferrer">{{ douyinShareUrl }}</a>
                      </b>
                    </div>
                    <a-button
                      v-if="douyinShareUrl"
                      block
                      style="margin-top: 8px"
                      @click="openDouyinShare"
                    >
                      在抖音查看作品
                    </a-button>
                  </div>
                  <div class="cc-douyin-stats">
                    <div class="cc-douyin-stats-head">
                      <span>数据表现</span>
                      <a-button size="small" :loading="douyinStatsLoading" @click="refreshDouyinStats">
                        刷新
                      </a-button>
                    </div>
                    <div v-if="douyinStatsSyncedAt" class="cc-douyin-stats-sync">
                      更新于 {{ douyinStatsSyncedAt }}
                    </div>
                    <div class="cc-douyin-stats-grid">
                      <div v-for="row in douyinStatsRows" :key="row.label" class="cc-douyin-stat">
                        <span>{{ row.label }}</span>
                        <b>{{ row.value }}</b>
                      </div>
                    </div>
                    <div class="cc-douyin-stats-note">
                      每小时自动同步。互动率 = (点赞+评论+分享)/播放。
                    </div>
                  </div>
                </div>

                <div v-else class="cc-video-section">
                  <div class="cc-section-label">③ 发布到抖音</div>
                  <a-alert
                    v-if="latestReadyVideo.douyinPublishing || douyinPublishing"
                    type="info"
                    message="正在上传并发布到抖音，请稍候…"
                    show-icon
                    style="margin-bottom: 12px"
                  />
                  <a-alert
                    v-else-if="latestReadyVideo.douyinPublishError"
                    type="error"
                    :message="latestReadyVideo.douyinPublishError"
                    show-icon
                    style="margin-bottom: 12px"
                  />
                  <div v-else class="cc-douyin-bind">
                    <span v-if="douyinAccount">
                      将发布到：{{ douyinAccount.nickname || douyinAccount.openId }}
                    </span>
                    <span v-else class="douyin-store-unbound">
                      {{ storeLabel }} 尚未绑定抖音号
                      <a @click="bindCaseStoreDouyin">去绑定</a>
                    </span>
                  </div>
                  <a-button
                    block
                    type="primary"
                    style="margin-top: 12px"
                    :loading="douyinPublishing || latestReadyVideo.douyinPublishing"
                    :disabled="!douyinAccount || !isDouyinConfigured || latestReadyVideo.douyinPublishing"
                    @click="publishDouyin"
                  >
                    {{ latestReadyVideo.douyinPublishing || douyinPublishing ? '发布中…' : '发布到抖音' }}
                  </a-button>
                  <div class="cc-video-hint" style="margin-top: 8px">
                    发布后可在上方查看抖音链接与播放数据；审核通过前仅作者可见。
                  </div>
                </div>

                <div v-if="!isDouyinPublished" class="cc-video-section">
                  <div class="cc-section-label">④ 成片管理</div>
                  <a-button block :loading="videoGenerating" @click="regenerateVideo">
                    重新生成短视频
                  </a-button>
                  <div class="cc-video-hint">会重新排队渲染，并自动更新抖音文案。</div>
                </div>
              </template>

              <template v-else-if="latestVideo && (videoStatus === 'pending' || videoStatus === 'rendering')">
                <div class="cc-video-hint">{{ videoProgressText }}</div>
                <a-button block :loading="detailLoading" @click="refreshDetail">刷新状态</a-button>
              </template>

              <template v-else-if="latestVideo && videoStatus === 'failed'">
                <a-alert type="error" :message="latestVideo.error || '制作失败'" show-icon style="margin-bottom: 12px" />
                <a-button block type="primary" :loading="videoGenerating" @click="regenerateVideo">
                  重新生成
                </a-button>
              </template>

              <template v-else>
                <div class="cc-video-hint">
                  用本案例 15 段素材自动剪一条竖屏视频（配音 + 花字）。
                </div>
                <a-button block type="primary" :loading="videoGenerating" @click="generateVideo">
                  生成短视频
                </a-button>
              </template>
            </div>

            <div class="cc-panel cc-panel-codex">
              <div class="cc-panel-title">Codex 剪辑任务</div>
              <div class="cc-codex-hint">复制 JSON 给 Computer Use / Codex 自动剪辑。</div>
              <div class="cc-kv">
                <span>案例 ID</span>
                <b>
                  {{ currentCase.caseId }}
                  <a class="cc-inline-link" @click="copyCaseId">复制</a>
                </b>
              </div>
              <div class="cc-kv"><span>可剪视频</span><b>{{ codexClips.length }} 段</b></div>

              <div v-if="caseInfoRows.length" class="cc-case-info cc-case-info-codex">
                <div class="cc-case-info-title">案例资料</div>
                <div v-for="row in caseInfoRows" :key="'codex-' + row.label" class="cc-kv cc-kv-wide">
                  <span>{{ row.label }}</span>
                  <b>{{ row.value }}</b>
                </div>
                <div class="cc-other-notes-block">
                  <div class="cc-other-notes-label">其他</div>
                  <div :class="['cc-other-notes-body', { 'cc-kv-muted': !otherNotesText }]">
                    {{ otherNotesText || '（未填写）' }}
                  </div>
                </div>
              </div>

              <div v-if="codexClips.length" class="cc-codex-clips">
                <div v-for="clip in codexClips" :key="clip.shotCode" class="cc-codex-clip">
                  <div class="cc-codex-clip-head">
                    <span class="cc-codex-order">{{ clip.order }}</span>
                    <span class="cc-codex-name">{{ clip.shotName }}</span>
                    <span class="cc-codex-duration">{{ clipDurationLabel(clip) }}</span>
                  </div>
                  <div class="cc-codex-links">
                    <a :href="clip.sourceUrl" target="_blank">打开</a>
                    <a class="cc-inline-link" @click="copyText(clip.sourceUrl)">复制链接</a>
                  </div>
                </div>
              </div>
              <a-empty v-else :description="'还没有已上传的视频素材'" :image="null" />

              <a-button
                block
                type="primary"
                style="margin-top: 12px"
                :disabled="!codexClips.length"
                @click="copyCodexTask"
              >
                复制剪辑 JSON
              </a-button>
            </div>
          </div>
        </div>
      </a-spin>
    </a-drawer>

    <a-modal
      v-model:visible="douyinStoreModal"
      title="门店抖音账号"
      width="720px"
      :footer="null"
      @cancel="douyinStoreModal = false"
    >
      <a-alert
        v-if="!isDouyinConfigured"
        type="warning"
        show-icon
        message="服务端尚未配置抖音开放平台应用（DOUYIN_OPEN_CLIENT_KEY / SECRET）"
        style="margin-bottom: 12px"
      />
      <a-spin :spinning="douyinStoresLoading">
        <div v-for="row in douyinStores" :key="row.storeValue" class="douyin-store-row">
          <div class="douyin-store-main">
            <div class="douyin-store-meta">
              <b>{{ row.storeName }}</b>
              <span v-if="row.account" class="douyin-store-bound">
                已绑定：{{ row.account.nickname || row.account.openId }}
              </span>
              <span v-else class="douyin-store-unbound">未绑定</span>
            </div>
            <div v-if="row.account" class="douyin-store-poi">
              <a-input
                v-model:value="douyinPoiDrafts[row.storeValue].poiId"
                size="small"
                placeholder="POI ID（抖音门店定位）"
                style="width: 220px; margin-right: 8px"
              />
              <a-input
                v-model:value="douyinPoiDrafts[row.storeValue].poiName"
                size="small"
                placeholder="POI 名称（备注）"
                style="width: 180px; margin-right: 8px"
              />
              <a-button
                size="small"
                :loading="douyinPoiSaving === row.storeValue"
                @click="saveDouyinStorePoi(row.storeValue)"
              >
                保存 POI
              </a-button>
            </div>
          </div>
          <div class="douyin-store-actions">
            <a-button
              v-if="!row.account"
              size="small"
              type="primary"
              :loading="douyinBinding === row.storeValue"
              :disabled="!isDouyinConfigured"
              @click="bindDouyinStore(row.storeValue)"
            >
              绑定抖音号
            </a-button>
            <a-button
              v-else
              size="small"
              danger
              :loading="douyinBinding === row.storeValue"
              @click="unbindDouyinStore(row.storeValue)"
            >
              解绑
            </a-button>
          </div>
        </div>
      </a-spin>
    </a-modal>

  </div>
</template>

<script>
import {
  getContentCollectCases,
  getContentCollectCase,
  postContentCollectCaptureFeedback,
  postContentCollectCaseCaptureFeedback,
  reviewContentCollectCase,
  generateContentCollectVideo,
  publishContentCollectDouyin,
  updateContentCollectDouyinCaption,
  syncContentCollectDouyinStats,
  getContentCollectDouyinAccounts,
  getContentCollectDouyinConnectUrl,
  disconnectContentCollectDouyin,
  updateContentCollectDouyinStorePoi,
  getContentCollectSettings,
  updateContentCollectSettings,
  deleteContentCollectCases,
  deleteContentCollectAssets
} from '../http/api'
import { Modal } from 'ant-design-vue'
import GigaTable from '../components/GigaTable.vue'

const STATUS_TEXT = {
  uploaded: '已上传',
  pending: '未拍',
  retake_required: '待补拍'
}

const VIDEO_STATUS = {
  pending: { text: '排队中', color: 'processing' },
  rendering: { text: '制作中', color: 'processing' },
  ready: { text: '已完成', color: 'success' },
  failed: { text: '失败', color: 'error' }
}

const CUSTOMER_TYPE_TEXT = {
  new: '新客户',
  returning: '老客户'
}

export default {
  components: { GigaTable },
  data() {
    return {
      status: '',
      sortBy: 'created_desc',
      dataSource: [],
      loading: false,
      loadError: '',
      deleting: false,
      rowSelection: {
        selectedRowKeys: [],
        onChange: (keys) => {
          this.rowSelection.selectedRowKeys = keys
        }
      },
      showDetail: false,
      detailLoading: false,
      currentCase: null,
      pickedShotCodes: [],
      assetDeleting: false,
      retakeShotCodes: [],
      retakeNote: '',
      feedbackShotCode: '',
      feedbackSaving: false,
      feedbackForm: {
        verdict: 'pass',
        summary: '',
        issuesText: '',
        suggestionsText: '',
        markRetake: false
      },
      caseFeedbackForm: {
        summary: ''
      },
      caseFeedbackSaving: false,
      videoGenerating: false,
      douyinPublishing: false,
      douyinCaptionDraft: '',
      douyinCaptionSaving: false,
      douyinBinding: null,
      douyinStoreModal: false,
      douyinStoresLoading: false,
      douyinStores: [],
      douyinConfigured: false,
      douyinMiniprogramPublishEnabled: false,
      douyinMiniprogramPublishSaving: false,
      douyinPoiDrafts: {},
      douyinPoiSaving: null,
      douyinStatsLoading: false,
      columns: [{
        title: '案例ID',
        dataIndex: 'caseId',
        key: 'caseId',
        width: 220
      }, {
        title: '车型',
        dataIndex: 'vehicleTitle',
        key: 'vehicleTitle'
      }, {
        title: '产品',
        dataIndex: 'productName',
        key: 'productName',
        customRender: ({ record }) => (
          record.packageName
          || record.projectName
          || record.productName
          || '—'
        )
      }, {
        title: '进度',
        dataIndex: 'progress',
        key: 'progress',
        customRender: ({ record }) => `${record.progress?.completed || 0}/${record.progress?.required || 0}`
      }, {
        title: '状态',
        dataIndex: 'statusLabel',
        key: 'statusLabel'
      }, {
        title: '短视频',
        dataIndex: 'videoStatusLabel',
        key: 'videoStatusLabel',
        customRender: ({ record }) => record.videoStatusLabel || '未生成'
      }, {
        title: '抖音播放',
        dataIndex: 'douyinPlayCount',
        key: 'douyinPlayCount',
        width: 100,
        customRender: ({ record }) => (
          record.douyinPlayCount != null ? record.douyinPlayCount : '—'
        )
      }, {
        title: '抖音作品',
        dataIndex: 'douyinShareUrl',
        key: 'douyinShareUrl',
        width: 110,
        customRender: ({ record }) => (
          record.douyinShareUrl
            ? <a href={record.douyinShareUrl} target="_blank" rel="noopener noreferrer">查看</a>
            : '—'
        )
      }, {
        title: '互动率',
        dataIndex: 'douyinInteractRate',
        key: 'douyinInteractRate',
        width: 90,
        customRender: ({ record }) => (
          record.douyinInteractRate != null ? `${record.douyinInteractRate}%` : '—'
        )
      }, {
        title: '操作',
        dataIndex: 'operation',
        key: 'operation',
        width: 160,
        customRender: (val) => {
          return (
            <span>
              <a onClick={() => this.openDetail(val.record)}>查看素材</a>
              <a
                style="margin-left: 12px; color: #ff4d4f"
                onClick={() => this.deleteCases([val.record.caseId])}
              >
                删除
              </a>
            </span>
          )
        }
      }]
    }
  },
  computed: {
    // 表格的 rowKey 是数字 id，但删除接口按 caseId 走，这里换回来。
    selectedCaseIds() {
      const keys = new Set(this.rowSelection.selectedRowKeys)
      return this.dataSource.filter(item => keys.has(item.id)).map(item => item.caseId)
    },
    submitterText() {
      const s = this.currentCase?.submitter || {}
      const name = s.name || s.nickname
      if (name && s.mobile) return `${name}（${s.mobile}）`
      return name || s.mobile || '未知'
    },
    // 案例的 createdAt 是「开单」时间，真正开拍可能晚很多，所以拍摄时间
    // 按素材上传时间算。同一台车基本一场拍完，用「首条 ~ 末条」表示区间。
    shootTimeText() {
      const times = (this.currentCase?.assets || [])
        .map(a => a.capturedAt)
        .filter(Boolean)
        .sort()
      if (!times.length) return '未开拍'
      const first = this.timeText(times[0])
      const last = this.timeText(times[times.length - 1])
      return first === last ? first : `${first} ~ ${last.slice(-5)}`
    },
    detailTitle() {
      if (!this.currentCase) return '案例详情'
      return this.caseProjectSummary
        || `${this.currentCase.vehicleBrand || ''} ${this.currentCase.vehicleModel || ''}`.trim()
        || '案例详情'
    },
    caseProjectSummary() {
      const currentCase = this.currentCase
      if (!currentCase) return ''
      const legacy = (currentCase.projectSummary || '').trim()
      if (legacy && !this.vehicleLineText) return legacy
      const parts = []
      if (this.vehicleLineText) parts.push(this.vehicleLineText)
      const project = (currentCase.projectName || '').trim()
      if (project) parts.push(project)
      if (currentCase.dealPrice) parts.push(`成交价约 ${currentCase.dealPrice} 元`)
      if (parts.length) return parts.join('，')
      return legacy || project
    },
    vehicleLineText() {
      const currentCase = this.currentCase
      if (!currentCase) return ''
      const v = currentCase.vehicle || {}
      const fromVehicle = [v.brand, v.model].filter(Boolean).join(' ').trim()
      if (fromVehicle) return fromVehicle
      return [currentCase.vehicleBrand, currentCase.vehicleModel].filter(Boolean).join(' ').trim()
    },
    otherNotesText() {
      return (this.currentCase?.otherNotes || '').trim()
    },
    customerTypeText() {
      return CUSTOMER_TYPE_TEXT[this.currentCase?.customerType] || ''
    },
    leadSourceText() {
      const currentCase = this.currentCase
      if (!currentCase) return ''
      const source = (currentCase.leadSource || '').trim()
      if (!source) return ''
      if (source === '其他' && currentCase.leadSourceOther) {
        return `${source}：${currentCase.leadSourceOther}`
      }
      return source
    },
    caseInfoRows() {
      if (!this.currentCase) return []
      const currentCase = this.currentCase
      const rows = []
      const push = (label, value) => {
        const text = (value || '').toString().trim()
        if (text) rows.push({ label, value: text })
      }
      push('品牌车型', this.vehicleLineText)
      push('施工项目', currentCase.projectName || currentCase.packageName)
      push('成交价格', currentCase.dealPrice ? `${currentCase.dealPrice} 元` : '')
      push('车主姓名', currentCase.ownerName || currentCase.customerName)
      push('客户类型', this.customerTypeText)
      push('线索来源', this.leadSourceText)
      push('为什么选我们', currentCase.whyChooseUs)
      push('施工门店', currentCase.storeName || currentCase.construction?.store)
      push('施工日期', currentCase.construction?.date)
      if (!this.vehicleLineText && !currentCase.projectName) {
        push('车辆与项目（旧）', this.caseProjectSummary)
      }
      return rows
    },
    vehicleText() {
      return this.vehicleLineText || '待识别'
    },
    // 标准镜头按顺序排在前面，封面和额外视频跟在后面，和小程序的顺序一致。
    sortedAssets() {
      const assets = (this.currentCase?.assets || []).slice()
      const weight = { standard: 0, cover: 1, extra: 2 }
      assets.sort((a, b) => (weight[a.category] - weight[b.category]) || (a.shotOrder - b.shotOrder))
      let standardIndex = 0
      return assets.map(asset => {
        if (asset.category === 'standard') standardIndex += 1
        return {
          ...asset,
          displayIndex: asset.category === 'standard' ? `${standardIndex}.` : ''
        }
      })
    },
    feedbackShotOptions() {
      return this.uploadedAssets.map(asset => ({
        value: asset.shotCode,
        label: `${asset.displayIndex || ''} ${asset.shotName} (${asset.shotCode})`.trim()
      }))
    },
    uploadedAssets() {
      return this.sortedAssets.filter(a => a.status === 'uploaded')
    },
    videoAssets() {
      return this.uploadedAssets.filter(a => a.mediaType !== 'photo')
    },
    photoAssets() {
      return this.uploadedAssets.filter(a => a.mediaType === 'photo')
    },
    // 待补拍已在红色汇总里展示，不能再算进「还缺 N 项」，否则会重复。
    missingAssets() {
      return this.sortedAssets.filter(a => (
        a.category !== 'extra'
        && a.status !== 'uploaded'
        && a.status !== 'retake_required'
      ))
    },
    retakeRequiredAssets() {
      return this.sortedAssets.filter(a => a.status === 'retake_required')
    },
    isApprovedCase() {
      return this.currentCase?.status === 'approved'
    },
    showCaptureFeedbackPanel() {
      return !!this.currentCase
    },
    showVideoSidePanel() {
      const status = this.currentCase?.status
      return status === 'approved' || status === 'pending_review'
    },
    showCaseAlert() {
      return this.retakeRequiredAssets.length > 0 || this.missingAssets.length > 0
    },
    caseAlertMessage() {
      const parts = []
      if (this.retakeRequiredAssets.length) parts.push(`待补拍 ${this.retakeRequiredAssets.length} 镜`)
      if (this.missingAssets.length) parts.push(`还缺 ${this.missingAssets.length} 项`)
      return parts.join(' · ')
    },
    caseAlertType() {
      return this.retakeRequiredAssets.length ? 'error' : 'warning'
    },
    script() {
      return this.currentCase?.videoScript?.data || {}
    },
    latestVideo() {
      return (this.currentCase?.videos || [])[0] || null
    },
    latestReadyVideo() {
      return (this.currentCase?.videos || []).find(v => v.status === 'ready') || null
    },
    publishedDouyinVideo() {
      return (this.currentCase?.videos || []).find(v => v.douyinItemId) || null
    },
    isDouyinPublished() {
      return !!(this.latestReadyVideo && this.latestReadyVideo.douyinItemId)
    },
    douyinDisplayVideo() {
      if (this.latestReadyVideo && this.latestReadyVideo.douyinItemId) {
        return this.latestReadyVideo
      }
      return this.publishedDouyinVideo || this.latestReadyVideo
    },
    canEditDouyinCaption() {
      return !!(this.latestReadyVideo && !this.isDouyinPublished)
    },
    isRegeneratingVideo() {
      const latest = this.latestVideo
      return !!(latest && latest.status !== 'ready' && this.latestReadyVideo)
    },
    douyinShareUrl() {
      const video = this.douyinDisplayVideo
      if (!video) return ''
      return video.douyinShareUrl || video.douyinUrl || ''
    },
    storeValue() {
      return this.currentCase?.construction?.storeValue ?? this.currentCase?.storeValue
    },
    storeLabel() {
      return this.currentCase?.construction?.store
        || this.currentCase?.storeName
        || (this.storeValue ? `门店${this.storeValue}` : '本店')
    },
    douyinAccount() {
      return this.currentCase?.douyinAccount || null
    },
    isDouyinConfigured() {
      if (this.currentCase && typeof this.currentCase.douyinConfigured === 'boolean') {
        return this.currentCase.douyinConfigured
      }
      return this.douyinConfigured
    },
    douyinStats() {
      return this.douyinDisplayVideo?.douyinStats || {}
    },
    douyinVideoForActions() {
      return this.latestReadyVideo
    },
    douyinStatsSyncedAt() {
      const iso = this.douyinStats.syncedAt
      return iso ? this.timeText(iso) : ''
    },
    douyinStatsRows() {
      const s = this.douyinStats
      const fmt = (n) => (n == null ? '—' : n)
      const pct = (n) => (n == null ? '—' : `${n}%`)
      return [
        { label: '播放', value: fmt(s.playCount) },
        { label: '点赞', value: fmt(s.diggCount) },
        { label: '评论', value: fmt(s.commentCount) },
        { label: '分享', value: fmt(s.shareCount) },
        { label: '点赞率', value: pct(s.likeRate) },
        { label: '评论率', value: pct(s.commentRate) },
        { label: '分享率', value: pct(s.shareRate) },
        { label: '互动率', value: pct(s.interactRate) }
      ]
    },
    videoStatus() {
      return this.latestVideo?.status || ''
    },
    videoStatusText() {
      return (VIDEO_STATUS[this.videoStatus] || {}).text || this.videoStatus
    },
    videoStatusColor() {
      return (VIDEO_STATUS[this.videoStatus] || {}).color || 'default'
    },
    readyVideoDurationText() {
      const ms = this.latestReadyVideo?.durationMs
      if (!ms) return '—'
      return `${(ms / 1000).toFixed(1)}s`
    },
    videoDurationText() {
      const ms = this.latestVideo?.durationMs
      if (!ms) return '—'
      return `${(ms / 1000).toFixed(1)}s`
    },
    videoProgressText() {
      if (this.videoStatus === 'rendering') {
        return '正在制作，通常几分钟内完成。可点下面刷新查看进度。'
      }
      return '已提交制作任务，排队中。服务器空闲后会开始，大约 10 分钟内完成。'
    },
    codexClips() {
      if (!this.currentCase) return []
      const scriptMap = {}
      ;(this.script.shots || []).forEach((shot) => {
        scriptMap[shot.shotCode] = shot
      })

      const clips = []
      let order = 0
      this.sortedAssets.forEach((asset) => {
        if (asset.status !== 'uploaded' || !asset.fileUrl) return
        if (asset.mediaType === 'photo') return

        order += 1
        const scriptShot = scriptMap[asset.shotCode] || {}
        clips.push({
          order,
          shotCode: asset.shotCode,
          shotName: asset.shotName,
          category: asset.category,
          mediaType: asset.mediaType || 'video',
          sourceUrl: asset.fileUrl,
          sourceDurationMs: asset.durationMs || null,
          sourceDurationSec: asset.durationMs ? +(asset.durationMs / 1000).toFixed(1) : null,
          targetSeconds: scriptShot.seconds || null,
          narration: scriptShot.narration || asset.voiceTranscript || '',
          caption: scriptShot.caption || '',
          voiceTranscript: asset.voiceTranscript || '',
        })
      })
      return clips
    },
    codexCoverPhoto() {
      const cover = this.sortedAssets.find((asset) => (
        asset.category === 'cover' && asset.status === 'uploaded' && asset.fileUrl
      ))
      if (!cover) return null
      return {
        shotCode: cover.shotCode,
        shotName: cover.shotName,
        sourceUrl: cover.fileUrl,
      }
    },
    codexTask() {
      if (!this.currentCase) return null
      const vehicle = this.vehicleText
      const product = this.currentCase.packageName || this.currentCase.productName || this.currentCase.productTypeLabel || ''
      return {
        version: 1,
        purpose: 'codex-video-edit',
        caseId: this.currentCase.caseId,
        status: this.currentCase.status,
        title: this.script.title || this.caseProjectSummary || `${this.currentCase.vehicleBrand || ''} ${this.currentCase.vehicleModel || ''}`.trim() || this.currentCase.caseId,
        hook: this.script.hook || '',
        cta: this.script.cta || '',
        hashtags: this.script.hashtags || [],
        vehicle,
        product,
        dealPrice: this.currentCase.dealPrice || '',
        caseInfo: {
          projectSummary: this.caseProjectSummary,
          ownerName: this.currentCase.ownerName || this.currentCase.customerName || '',
          customerType: this.customerTypeText,
          leadSource: this.leadSourceText,
          whyChooseUs: this.currentCase.whyChooseUs || '',
          packageName: this.currentCase.packageName || this.currentCase.productName || '',
          storeName: this.currentCase.storeName || this.currentCase.construction?.store || '',
          constructionDate: this.currentCase.construction?.date || '',
          dealPrice: this.currentCase.dealPrice || '',
        },
        clipCount: this.codexClips.length,
        clips: this.codexClips,
        coverPhoto: this.codexCoverPhoto,
        instructions: [
          '按 clips 数组的 order 顺序剪辑',
          '每段视频裁到 targetSeconds；没有 targetSeconds 时保留原片或裁到 6 秒内',
          '画面字幕用 caption，口播参考 narration',
          '开头用 hook，结尾用 cta',
        ],
      }
    }
  },
  created() {
    this.loadCases()
    this.loadContentCollectSettings()
    this.handleDouyinOAuthReturn()
  },
  methods: {
    statusText(status) {
      return STATUS_TEXT[status] || status
    },
    statusColor(status) {
      if (status === 'uploaded') return 'green'
      if (status === 'retake_required') return 'red'
      return 'default'
    },
    durationText(asset) {
      if (asset.mediaType === 'photo') return '照片'
      if (!asset.durationMs) return asset.durationHint || ''
      return `${(asset.durationMs / 1000).toFixed(1)}s`
    },
    pickRetake(asset) {
      const codes = this.retakeShotCodes.slice()
      const at = codes.indexOf(asset.shotCode)
      if (at >= 0) codes.splice(at, 1)
      else codes.push(asset.shotCode)
      this.retakeShotCodes = codes
    },
    timeText(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    togglePick(shotCode) {
      const picked = this.pickedShotCodes
      const at = picked.indexOf(shotCode)
      if (at >= 0) picked.splice(at, 1)
      else picked.push(shotCode)
    },
    deleteAssets(shotCodes) {
      if (!shotCodes.length || !this.currentCase) return
      const names = (this.currentCase.assets || [])
        .filter(a => shotCodes.includes(a.shotCode))
        .map(a => a.shotName || a.shotCode)
      Modal.confirm({
        title: `确认删除 ${shotCodes.length} 条素材？`,
        content: `${names.join('、')}。标准镜头会退回「未拍」等员工重拍，额外镜头直接移除。`,
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          this.assetDeleting = true
          return deleteContentCollectAssets(this.currentCase.caseId, shotCodes).then(res => {
            if (!res.success) {
              this.$message.error(res.message || '删除失败')
              return
            }
            this.$message.success('已删除')
            this.currentCase = res.data.case
            this.pickedShotCodes = []
            this.loadCases()
          }).catch(err => {
            this.$message.error((err && err.message) || '删除失败')
          }).finally(() => {
            this.assetDeleting = false
          })
        }
      })
    },
    deleteSelected() {
      this.deleteCases(this.selectedCaseIds)
    },
    deleteCases(caseIds) {
      if (!caseIds.length) return
      const label = caseIds.length === 1 ? caseIds[0] : `这 ${caseIds.length} 条案例`
      Modal.confirm({
        title: '确认删除？',
        content: `将删除 ${label}，连同已上传的素材一起从列表中移除。`,
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          this.deleting = true
          return deleteContentCollectCases(caseIds).then(res => {
            if (!res.success) {
              this.$message.error(res.message || '删除失败')
              return
            }
            this.$message.success(`已删除 ${res.data.deleted.length} 条`)
            this.rowSelection.selectedRowKeys = []
            this.loadCases()
          }).catch(err => {
            this.$message.error((err && err.message) || '删除失败')
          }).finally(() => {
            this.deleting = false
          })
        }
      })
    },
    loadCases() {
      this.loading = true
      this.loadError = ''
      getContentCollectCases({
        status: this.status || undefined,
        sortBy: this.sortBy || undefined
      }).then(res => {
        if (!res.success) {
          // 接口失败时把原因留在页面上。之前只弹一条会自动消失的 toast，
          // 表格停在「暂无数据」，看起来和「真的没有案例」完全一样。
          this.dataSource = []
          this.loadError = res.message || '加载失败'
          return
        }
        this.dataSource = (res.data.cases || []).map(item => ({
          ...item,
          vehicleTitle: `${item.vehicleBrand || ''} ${item.vehicleModel || ''}`.trim()
        }))
      }).catch(err => {
        this.dataSource = []
        this.loadError = (err && err.message) || '加载失败，请检查网络后重试'
      }).finally(() => {
        this.loading = false
      })
    },
    openDetail(record) {
      this.showDetail = true
      this.detailLoading = true
      this.retakeShotCodes = []
      this.retakeNote = ''
      this.pickedShotCodes = []
      getContentCollectCase(record.caseId).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '加载详情失败')
          return
        }
        this.currentCase = res.data.case
        this.syncDouyinCaptionDraft()
        if (this.latestReadyVideo && !this.latestReadyVideo.douyinCaption && !this.latestReadyVideo.douyinItemId) {
          this.regenerateDouyinCaption({ silent: true })
        }
      }).catch(err => {
        this.$message.error(err.message || '加载详情失败')
      }).finally(() => {
        this.detailLoading = false
      })
    },
    refreshDetail() {
      if (!this.currentCase) return Promise.resolve()
      return getContentCollectCase(this.currentCase.caseId).then(res => {
        if (res.success) {
          this.currentCase = res.data.case
          this.syncDouyinCaptionDraft()
        }
      })
    },
    generateVideo(regenerate = false) {
      this.videoGenerating = true
      generateContentCollectVideo(this.currentCase.caseId, { regenerate }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '提交失败')
          return
        }
        this.$message.success(regenerate ? '已提交重新生成' : '已开始制作，稍后点刷新或等待自动更新')
        this.currentCase = res.data.case
        this.pollVideo(12)
      }).catch(err => {
        this.$message.error(err.message || '提交失败')
      }).finally(() => {
        this.videoGenerating = false
      })
    },
    regenerateVideo() {
      this.generateVideo(true)
    },
    handleDouyinOAuthReturn() {
      const query = this.$route?.query || {}
      if (!query.douyin) return
      if (query.douyin === 'bound') {
        this.$message.success(`${query.nickname || '抖音号'} 绑定成功`)
      } else if (query.douyin === 'error') {
        this.$message.error(decodeURIComponent(query.reason || '绑定失败'))
      }
      this.$router.replace({ path: this.$route.path, query: {} })
    },
    loadContentCollectSettings() {
      return getContentCollectSettings().then(res => {
        if (!res.success) return
        this.douyinMiniprogramPublishEnabled = !!res.data.douyinMiniprogramPublishEnabled
      }).catch(() => {})
    },
    toggleDouyinMiniprogramPublish(checked) {
      this.douyinMiniprogramPublishSaving = true
      return updateContentCollectSettings({
        douyinMiniprogramPublishEnabled: checked
      }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '保存失败')
          return
        }
        this.douyinMiniprogramPublishEnabled = !!res.data.douyinMiniprogramPublishEnabled
        this.$message.success(checked ? '已开启小程序抖音发布' : '已关闭小程序抖音发布')
      }).catch(err => {
        this.$message.error(err.message || '保存失败')
      }).finally(() => {
        this.douyinMiniprogramPublishSaving = false
      })
    },
    syncDouyinPoiDrafts(stores = []) {
      const drafts = {}
      stores.forEach(row => {
        const account = row.account || {}
        const existing = this.douyinPoiDrafts[row.storeValue] || {}
        drafts[row.storeValue] = {
          poiId: account.poiId ?? existing.poiId ?? '',
          poiName: account.poiName ?? existing.poiName ?? ''
        }
      })
      this.douyinPoiDrafts = drafts
    },
    saveDouyinStorePoi(storeValue) {
      const draft = this.douyinPoiDrafts[storeValue] || {}
      this.douyinPoiSaving = storeValue
      return updateContentCollectDouyinStorePoi({
        storeValue,
        poiId: draft.poiId || '',
        poiName: draft.poiName || ''
      }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '保存 POI 失败')
          return
        }
        this.$message.success('POI 已保存')
        this.douyinStores = res.data.stores || []
        this.syncDouyinPoiDrafts(this.douyinStores)
      }).catch(err => {
        this.$message.error(err.message || '保存 POI 失败')
      }).finally(() => {
        this.douyinPoiSaving = null
      })
    },
    openDouyinStores() {
      this.douyinStoreModal = true
      this.loadDouyinStores()
    },
    loadDouyinStores() {
      this.douyinStoresLoading = true
      return getContentCollectDouyinAccounts().then(res => {
        if (!res.success) {
          this.$message.error(res.message || '加载门店抖音账号失败')
          return
        }
        this.douyinConfigured = !!res.data.configured
        this.douyinStores = res.data.stores || []
        this.syncDouyinPoiDrafts(this.douyinStores)
      }).catch(err => {
        this.$message.error(err.message || '加载门店抖音账号失败')
      }).finally(() => {
        this.douyinStoresLoading = false
      })
    },
    bindDouyinStore(storeValue) {
      this.douyinBinding = storeValue
      return getContentCollectDouyinConnectUrl(storeValue).then(res => {
        if (!res.success || !res.data?.url) {
          this.$message.error(res.message || '获取授权链接失败')
          return
        }
        window.open(res.data.url, '_blank', 'noopener,noreferrer')
        this.$message.info('请在新窗口扫码授权，完成后关闭窗口并刷新列表')
      }).catch(err => {
        this.$message.error(err.message || '获取授权链接失败')
      }).finally(() => {
        this.douyinBinding = null
      })
    },
    bindCaseStoreDouyin() {
      const storeValue = this.storeValue
      if (!storeValue) {
        this.$message.warning('案例未设置施工门店，无法绑定')
        return
      }
      this.bindDouyinStore(storeValue).then(() => {
        this.openDouyinStores()
      })
    },
    unbindDouyinStore(storeValue) {
      this.douyinBinding = storeValue
      return disconnectContentCollectDouyin(storeValue).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '解绑失败')
          return
        }
        this.$message.success('已解绑')
        this.douyinStores = res.data.stores || []
        if (this.currentCase && this.storeValue === storeValue) {
          this.refreshDetail()
        }
      }).catch(err => {
        this.$message.error(err.message || '解绑失败')
      }).finally(() => {
        this.douyinBinding = null
      })
    },
    syncDouyinCaptionDraft() {
      this.douyinCaptionDraft = this.latestReadyVideo?.douyinCaption || ''
    },
    openDouyinShare() {
      if (!this.douyinShareUrl) return
      window.open(this.douyinShareUrl, '_blank', 'noopener,noreferrer')
    },
    saveDouyinCaption() {
      if (!this.currentCase || !this.douyinVideoForActions) return Promise.resolve()
      this.douyinCaptionSaving = true
      return updateContentCollectDouyinCaption(this.currentCase.caseId, {
        videoId: this.douyinVideoForActions.videoId,
        caption: this.douyinCaptionDraft
      }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '保存失败')
          return
        }
        this.currentCase = res.data.case
        this.syncDouyinCaptionDraft()
        this.$message.success('抖音文案已保存')
      }).catch(err => {
        this.$message.error(err.message || '保存失败')
      }).finally(() => {
        this.douyinCaptionSaving = false
      })
    },
    regenerateDouyinCaption(options = {}) {
      if (!this.currentCase || !this.douyinVideoForActions) return Promise.resolve()
      this.douyinCaptionSaving = true
      return updateContentCollectDouyinCaption(this.currentCase.caseId, {
        videoId: this.douyinVideoForActions.videoId,
        regenerate: true
      }).then(res => {
        if (!res.success) {
          if (!options.silent) this.$message.error(res.message || '生成失败')
          return
        }
        this.currentCase = res.data.case
        this.syncDouyinCaptionDraft()
        if (!options.silent) this.$message.success('已重新生成抖音文案')
      }).catch(err => {
        if (!options.silent) this.$message.error(err.message || '生成失败')
      }).finally(() => {
        this.douyinCaptionSaving = false
      })
    },
    publishDouyin() {
      if (!this.currentCase || !this.douyinVideoForActions || this.isDouyinPublished) return
      const video = this.douyinVideoForActions
      const saveCaption = this.douyinCaptionDraft !== (video.douyinCaption || '')
        ? this.saveDouyinCaption()
        : Promise.resolve()
      this.douyinPublishing = true
      saveCaption.then(() => publishContentCollectDouyin(this.currentCase.caseId, {
        videoId: video.videoId
      })).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '发布失败')
          return
        }
        this.$message.success('已提交抖音发布（审核通过前仅作者可见）')
        this.currentCase = res.data.case
        this.syncDouyinCaptionDraft()
      }).catch(err => {
        this.$message.error(err.message || '发布失败')
      }).finally(() => {
        this.douyinPublishing = false
      })
    },
    refreshDouyinStats() {
      const video = this.douyinDisplayVideo
      if (!this.currentCase || !video?.douyinItemId) return
      this.douyinStatsLoading = true
      syncContentCollectDouyinStats(this.currentCase.caseId, {
        videoId: video.videoId
      }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '刷新失败')
          return
        }
        this.$message.success('抖音数据已更新')
        this.currentCase = res.data.case
      }).catch(err => {
        this.$message.error(err.message || '刷新失败')
      }).finally(() => {
        this.douyinStatsLoading = false
      })
    },
    pollVideo(remaining) {
      if (remaining <= 0) return
      setTimeout(() => {
        this.refreshDetail().then(() => {
          const status = this.latestVideo?.status
          if (status === 'pending' || status === 'rendering') {
            this.pollVideo(remaining - 1)
          }
        })
      }, 15000)
    },
    clipDurationLabel(clip) {
      const source = clip.sourceDurationSec ? `${clip.sourceDurationSec}s` : '未知'
      const target = clip.targetSeconds ? `${clip.targetSeconds}s` : '?'
      return `${source} → ${target}`
    },
    copyCaseId() {
      this.copyText(this.currentCase.caseId, '案例 ID 已复制')
    },
    copyCodexTask() {
      if (!this.codexTask) return
      this.copyText(JSON.stringify(this.codexTask, null, 2), '剪辑 JSON 已复制')
    },
    copyText(text, successMessage = '已复制') {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success(successMessage)
      }).catch(() => {
        this.$message.error('复制失败，请手动选择')
      })
    },
    approveCase() {
      reviewContentCollectCase(this.currentCase.caseId, { action: 'approve' }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '操作失败')
          return
        }
        this.$message.success('已标记为素材合格')
        this.currentCase = res.data.case
        this.loadCases()
      })
    },
    captureFeedbackLabel(verdict) {
      return { pass: '可用', retake: '建议重拍', review: '待确认' }[verdict] || verdict
    },
    captureFeedbackColor(verdict) {
      return { pass: 'success', retake: 'error', review: 'processing' }[verdict] || 'default'
    },
    splitFeedbackLines(text) {
      return String(text || '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
    },
    submitCaseCaptureFeedback() {
      if (!this.currentCase) return
      const summary = (this.caseFeedbackForm.summary || '').trim()
      if (!summary) {
        this.$message.warning('请填写整单总评')
        return
      }
      this.caseFeedbackSaving = true
      postContentCollectCaseCaptureFeedback(this.currentCase.caseId, {
        summary,
        source: 'admin'
      }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '写入失败')
          return
        }
        this.$message.success('整单反馈已保存')
        this.refreshDetail()
      }).finally(() => {
        this.caseFeedbackSaving = false
      })
    },
    submitCaptureFeedback() {
      if (!this.currentCase || !this.feedbackShotCode) return
      const summary = (this.feedbackForm.summary || '').trim()
      if (!summary) {
        this.$message.warning('请填写一句话结论')
        return
      }
      this.feedbackSaving = true
      postContentCollectCaptureFeedback(this.currentCase.caseId, this.feedbackShotCode, {
        verdict: this.feedbackForm.verdict,
        summary,
        issues: this.splitFeedbackLines(this.feedbackForm.issuesText),
        suggestions: this.splitFeedbackLines(this.feedbackForm.suggestionsText),
        source: 'admin',
        markRetake: this.feedbackForm.markRetake
      }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '写入失败')
          return
        }
        this.$message.success('拍摄反馈已写入')
        this.refreshDetail()
      }).finally(() => {
        this.feedbackSaving = false
      })
    },
    retakeCase() {
      if (!this.retakeShotCodes.length) {
        this.$message.warning('请先选择要补拍的镜头')
        return
      }
      const count = this.retakeShotCodes.length
      if (!window.confirm(`确认要求补拍 ${count} 个镜头？`)) return
      reviewContentCollectCase(this.currentCase.caseId, {
        action: 'retake',
        retakes: this.retakeShotCodes.map(shotCode => ({
          shotCode,
          note: this.retakeNote
        }))
      }).then(res => {
        if (!res.success) {
          this.$message.error(res.message || '操作失败')
          return
        }
        this.$message.success('已要求补拍')
        this.retakeShotCodes = []
        this.retakeNote = ''
        this.currentCase = res.data.case
        this.loadCases()
      })
    }
  }
}
</script>

<style scoped lang="scss">
.operations {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.miniprogram-publish-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  color: rgba(0, 0, 0, 0.65);
  white-space: nowrap;
}

.cc-drawer-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}
.cc-drawer-sub {
  margin-top: 4px;
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
}

.cc-body {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.cc-main {
  flex: 1;
  min-width: 0;
}
.cc-side {
  width: 340px;
  flex-shrink: 0;
}

.cc-section-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
}
.cc-section-title {
  font-size: 16px;
  font-weight: 600;
}
.cc-section-sub {
  flex: 1;
  color: #8c8c8c;
  font-size: 13px;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 14px;
}
.asset-card {
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s ease;
}
.asset-card-active {
  border-color: #ff4d4f;
}
.asset-card-retake {
  border-color: rgba(255, 77, 79, 0.72);
}
.asset-retake-note {
  margin-bottom: 6px;
  padding: 6px 8px;
  border-radius: 4px;
  background: #fff1f0;
  color: #cf1322;
  font-size: 12px;
  line-height: 1.5;
}
.retake-line + .retake-line {
  margin-top: 4px;
}
.asset-media {
  position: relative;
  background: #111;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* 勾选框浮在画面上，不占卡片高度 —— 素材墙一屏要放下十几个镜头。 */
.asset-pick {
  position: absolute;
  top: 6px;
  left: 8px;
  z-index: 2;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.45);
}
.asset-media video,
.asset-media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.empty-media {
  color: #888;
  font-size: 13px;
}
.asset-info {
  padding: 8px 10px;
}
.asset-name {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
}
.asset-tags {
  margin-bottom: 6px;
}
/* 这一镜员工实际说的话，是 AI 写这镜旁白的主要依据，
   审核时能对照着看旁白有没有跑偏。 */
.asset-say {
  margin-bottom: 6px;
  padding: 6px 8px;
  border-radius: 4px;
  background: #f6f8fa;
  color: #595959;
  font-size: 12px;
  line-height: 1.5;
}
.asset-foot {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #8c8c8c;
}
.asset-links a {
  margin-left: 10px;
}
.asset-links a.danger {
  color: #ff4d4f;
}

.cc-panel {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 14px;
}
.cc-panel-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.cc-kv {
  display: flex;
  gap: 10px;
  font-size: 13px;
  line-height: 1.9;
}
.cc-kv span {
  color: #8c8c8c;
  width: 56px;
  flex-shrink: 0;
}
.cc-kv b {
  font-weight: 500;
  word-break: break-all;
}
.cc-kv-wide span {
  width: 88px;
}
.cc-case-info {
  margin-top: 16px;
}
.cc-case-info-codex {
  margin-top: 10px;
  padding: 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #eef2f8;
}
.cc-case-info-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}
.cc-other-notes-block {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e8e8e8;
}
.cc-other-notes-label {
  color: #8c8c8c;
  font-size: 12px;
  margin-bottom: 6px;
}
.cc-other-notes-body {
  color: #262626;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
.cc-kv-muted {
  color: #bfbfbf !important;
  font-weight: 400 !important;
}

.cc-feedback-subtitle {
  margin: 12px 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
}
.cc-case-feedback-read {
  margin: 12px 0;
  padding: 10px 12px;
  background: #f6f8fa;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
}
.cc-case-feedback-read-title {
  font-weight: 600;
  margin-bottom: 6px;
}
.cc-panel-feedback .cc-hint-block {
  margin-bottom: 12px;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.5;
}
.cc-panel-feedback code {
  font-size: 11px;
  word-break: break-all;
}
.asset-capture-feedback {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f6ffed;
  border: 1px solid #d9f7be;
  font-size: 12px;
  line-height: 1.5;
}
.asset-capture-feedback-sub {
  margin-top: 4px;
  color: #595959;
}
.asset-capture-feedback-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.asset-capture-feedback-source {
  color: #8c8c8c;
  font-size: 11px;
}

.cc-panel-review {
  border-color: #91caff;
  background: #f0f7ff;
}
.cc-panel-review .cc-panel-title {
  color: #0958d9;
}
.cc-panel-video {
  border-color: #95de64;
  background: #f6ffed;
}
.cc-panel-video .cc-panel-title {
  color: #389e0d;
}
.cc-video-hint {
  color: #595959;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 12px;
}
.cc-panel-codex {
  background: #fafafa;
}
.cc-codex-hint {
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 10px;
}
.cc-codex-clips {
  margin-top: 10px;
  max-height: 240px;
  overflow: auto;
}
.cc-codex-clip {
  padding: 10px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #eef2f8;
}
.cc-codex-clip-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.cc-codex-order {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #1677ff;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}
.cc-codex-name {
  flex: 1;
  min-width: 120px;
  font-size: 12px;
  font-weight: 600;
}
.cc-codex-duration {
  font-size: 11px;
  color: #8c8c8c;
  white-space: nowrap;
}
.cc-codex-line {
  font-size: 12px;
  color: #595959;
  line-height: 1.5;
  margin-bottom: 4px;
}
.cc-codex-links {
  margin-top: 6px;
  font-size: 12px;
}
.cc-codex-links a + a {
  margin-left: 10px;
}
.cc-inline-link {
  margin-left: 8px;
  font-size: 12px;
}
.douyin-store-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}
.douyin-store-main {
  flex: 1;
  min-width: 0;
}
.douyin-store-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.douyin-store-poi {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.cc-caption-tag-ai {
  color: #1677ff;
}
.douyin-store-bound {
  color: #389e0d;
  font-size: 12px;
}
.douyin-store-unbound {
  color: #8c8c8c;
  font-size: 12px;
}
.cc-douyin-bind,
.cc-douyin-published,
.cc-douyin-link-panel {
  margin-top: 12px;
  font-size: 12px;
  color: #595959;
}
.cc-douyin-link-panel {
  padding: 10px 12px;
  border-radius: 8px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}
.cc-douyin-caption {
  margin-top: 12px;
}
.cc-douyin-caption-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
}
.cc-caption-tag {
  font-size: 11px;
  color: #8c8c8c;
  font-weight: 400;
}
.cc-douyin-caption-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.cc-video-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.cc-video-section:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}
.cc-section-label {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #262626;
}
.cc-caption-readonly {
  padding: 10px 12px;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  font-size: 13px;
  line-height: 1.7;
  color: #434343;
  white-space: pre-wrap;
  word-break: break-all;
}
.cc-douyin-stats {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
}
.cc-douyin-stats-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}
.cc-douyin-stats-sync {
  font-size: 11px;
  color: #8c8c8c;
  margin-bottom: 8px;
}
.cc-douyin-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}
.cc-douyin-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  color: #8c8c8c;
}
.cc-douyin-stat b {
  font-size: 14px;
  color: #262626;
}
.cc-douyin-stats-note {
  margin-top: 8px;
  font-size: 11px;
  color: #bfbfbf;
  line-height: 1.4;
}
</style>
